import React, { useCallback, useEffect, useRef, useState } from "react";
import { parseFloatData } from "utils/UtilityFuncs";

export default function AutoComplete({
  required,
  type,
  is2digitFloat,
  fieldName,
  placeholder,
  handleChange,
  classNames,
  value,
  searchFunction, // Function to search data, required
  disabled = false,
  searchOnValueChange = false, // If true, search results will update on input change
  autoFocus = false, // If true, input will be focused on mount
}) {
  /*   All States
   ********************************************* */
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const autoCompleteRef = useRef();

  /*   All Functions
   ********************************************* */
  const getAllResultsFromField = async (fieldName) => {
    if (disabled) return;
    if (showSuggestions) {
      setShowSuggestions(false);
      return;
    }
    try {
      // When getting all results, pass empty string to get everything
      const results = await searchFunction("", fieldName);
      setSearchResults(results);
      setShowSuggestions(true);
      return results;
    } catch (err) {
      console.error("Error fetching all results:", err.message);
      setSearchResults([]);
    }
  };

  // Memoized search executor to satisfy exhaustive-deps and avoid re-creating on each render
  const getSearchResultsFromSearchFunc = async (text) => {
    try {
      const results = await searchFunction(text, fieldName);
      // remove duplicates
      const uniqueResults = results.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t[fieldName] === item[fieldName]),
      );
      setSearchResults(uniqueResults);
    } catch (err) {
      console.error("Error in search:", err.message);
      setSearchResults([]);
    }
  };

  /*   All UseEffects
   ********************************************* */

  // Close the suggestions when clicking outside of the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autoCompleteRef.current &&
        !autoCompleteRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search text changes with debounce
  useEffect(() => {
    let timeout = setTimeout(() => {
      if (!disabled && searchText !== "") {
        setSearchResults(null);
        getSearchResultsFromSearchFunc(searchText);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText, disabled]);

  // Handle Tab key press
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      setShowSuggestions(false);
    }
  };

  // If searchOnValueChange is true, update search results on input change
  useEffect(() => {
    if (searchOnValueChange) {
      setShowSuggestions(true);
      setSearchText(value);
    }
  }, [searchOnValueChange, value]);

  return (
    <div className="position-relative" ref={autoCompleteRef}>
      <>
        <input
          type={type || "text"}
          name={fieldName}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className={classNames}
          required={required}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) return;
            setShowSuggestions(true);
            setSearchText(e.target.value);
            handleChange(
              fieldName,
              is2digitFloat ? parseFloatData(e.target.value) : e.target.value,
            );
          }}
          onKeyDown={handleKeyDown}
          value={value}
        />
        <div
          onClick={() => !disabled && getAllResultsFromField(fieldName)}
          className="btn btn-light position-absolute end-0 top-0 h-100"
        >
          <i className="mdi mdi-chevron-down text-secondary"></i>
        </div>
      </>
      {showSuggestions && !disabled && searchResults && (
        <div
          className="d-flex flex-column bg-white suggestion-hover py-2 px-2 rounded-3 custom-shadow-sm"
          style={{
            position: "absolute",
            zIndex: 1000,
            width: "100%",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {searchResults.length > 0 ? (
            searchResults.map((item, index) => (
              <div key={index}>
                <button
                  type="button"
                  className="suggestion-hover btn btn-sm btn-light text-start w-100"
                  onClick={() => {
                    handleChange(fieldName, item[fieldName]);
                    setSearchText("");
                    setSearchResults(null);
                    setShowSuggestions(false);
                  }}
                >
                  {item[fieldName]}
                </button>
                {index !== searchResults.length - 1 && (
                  <hr
                    style={{
                      margin: "0",
                    }}
                  />
                )}
              </div>
            ))
          ) : (
            <div className="text-muted small px-1">No results found</div>
          )}
        </div>
      )}
    </div>
  );
}
